import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form } from "antd";
import { useTranslation } from "react-i18next";
import { AnyObject } from "yup";
import VaadCreateForm from "./Form";
import {
  useVaadCreate,
  useVaadUpdate,
  VaadCreateParams,
  VaadTableRow,
} from "../../../../hooks/api/vaads";
import { useMessageError, useMessageSuccess } from "../../../../hooks/common";
import { useNavigate } from "react-router";
import { useVaadTabs } from "../SinglePage/VaadTabs/context";

interface VaadModalProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  setTableKey?: () => void;
  initialState?: VaadTableRow | null;
}

export default function VaadCreateModal({
  open,
  mode,
  onClose,
  setTableKey,
  initialState,
}: VaadModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const initialValuesRef = useRef<AnyObject>(null);
  const [isDirty, setIsDirty] = useState(false);
  const vaadCreate = useVaadCreate();
  const vaadUpdate = useVaadUpdate();
  const navigate = useNavigate();
  const { setActiveTab } = useVaadTabs();

  const handleSubmit = async (values: AnyObject) => {
    const payload: VaadCreateParams = {
      name: values.name,
      status: values.status,
      country: values.country,
      city: values.city ?? "",
      state: values.state ?? "",
      address: values.address ?? "",
      defaultTime: values.defaultTime ?? "",
      defaultDay: values.defaultDay,
      timezone: values.timezone ?? "",
      type: values.type,
      ...(values.user ? { user: { id: values.user } } : {}),
      vaadId: values.vaadId ?? "",
    };

    try {
      if (mode === "edit" && initialState?.id) {
        const result = await vaadUpdate.fetch(payload, initialState.id);
        if (result instanceof Error || (result as any)?.isAxiosError) {
          throw result;
        }
      } else {
        const result = await vaadCreate.fetch(payload);
        if (result && !("isAxiosError" in result) && "id" in result) {
          setActiveTab("members");
          navigate(`${result.id}/view`);
        }
        if (result instanceof Error || (result as any)?.isAxiosError) {
          const axiosError = result as any;

          // if (axiosError.response?.status === 409) {
          //   const serverMessage =
          //     axiosError.response?.data?.message?.[0] ||
          //     axiosError.response?.data?.error ||
          //     "Vaad with this vaadId already exists";

          //   message.error(serverMessage);
          //   return;
          // }

          throw result;
        }
      }

      form.resetFields();
      onClose();
      setTableKey?.();
    } catch (error: any) {
      // message.error("Something went wrong");
      console.error(error);
    }
  };
  useMessageSuccess([vaadCreate], "Vaad created successfully");
  useMessageError([vaadCreate]);
  useMessageSuccess([vaadUpdate], "Vaad updated successfully");
  useMessageError([vaadUpdate]);

  const handleClose = () => {
    form.resetFields();
    initialValuesRef.current = null;
    setIsDirty(false);
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialState) {
      form.setFieldsValue({
        ...initialState,
        user: initialState.user?.id,
      });
      initialValuesRef.current = {
        ...initialState,
        user: initialState.user ?? null,
      };
      setIsDirty(false);
    }

    if (mode === "create") {
      initialValuesRef.current = null;
      setIsDirty(true);
    }
  }, [open, mode, initialState]);

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      {t("common.cancel")}
    </Button>,
    <Button
      key="create"
      type="primary"
      loading={vaadCreate.loading || vaadUpdate.loading}
      disabled={vaadCreate.loading || vaadUpdate.loading}
      onClick={() => {
        form
          .validateFields()
          .then((values) => {
            form.submit();
          })
          .catch((info) => {
            console.log("Validation failed:", info);
          });
      }}
    >
      {mode === "edit" ? "Save Changes" : "Create Vaad"}
    </Button>,
  ];

  return (
    <Modal
      title={mode === "edit" ? "Edit Vaad" : "Add New Vaad"}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={520}
      footer={footer}
    >
      <Form
        form={form}
        layout="vertical"
        name="vaadForm"
        onFinish={handleSubmit}
        onValuesChange={(_, allValues) => {
          if (mode !== "edit") return;
          const isChanged =
            JSON.stringify(allValues) !==
            JSON.stringify(initialValuesRef.current);
          setIsDirty(isChanged);
        }}
      >
        <VaadCreateForm initialUser={initialState?.user} mode={mode} />
      </Form>
    </Modal>
  );
}
